import { BaseEntityModel } from '/@/modules/core/app/models/abstracts/BaseEntityModel';
import { ModelType } from '/@/modules/core/app/constants/ModelType';
import { DocumentSubType } from '/@/modules/document/enums/DocumentSubType';
import { EstateTaskTaxFormStatus } from '/@/modules/estatetask/enums/EstateTaskTaxFormStatus';
import { EstateTaxFormCanadaMeta } from '@/modules/estatetaxform/models/meta/EstateTaxFormCanadaMeta';
import { EstateTaxFormUsMeta } from '@/modules/estatetaxform/models/meta/EstateTaxFormUsMeta';
import { Document } from '@/modules/document/models/Document';
import { EstateTask } from '@/modules/estatetask/models/EstateTask';
import { EstateTaxFormMeta } from '@/modules/estatetaxform/models/meta/EstateTaxFormMeta';
import { EsignEnvelopeEstateAccount } from '@/modules/esign/models/EsignEnvelopeEstateAccount';
import { EstateTaxFormType } from '@/modules/estatetaxform/enums/EstateTaxFormType';
import { EstateTaxFormMetaType } from '@/modules/estatetaxform/constants/EstateTaxFormMetaType';

export class EstateTaxForm extends BaseEntityModel {
  id?: number;
  estateId?: number;
  estateTaxFilingId?: number;
  name?: string;
  type?: string;
  metaType?: string;
  isRequired?: boolean;
  filedDate?: string;
  interestPenaltyAmount?: number;
  year?: number;
  documents?: Relationship<Document[]> | null;
  estateTask?: Relationship<EstateTask> | null;
  meta?: Relationship<EstateTaxFormCanadaMeta | EstateTaxFormUsMeta> | null;

  constructor(model: Partial<EstateTaxForm> = {}) {
    super(model);

    this.id = model.id;
    this.estateId = model.estateId;
    this.estateTaxFilingId = model.estateTaxFilingId;
    this.name = model.name;
    this.type = model.type;
    this.metaType = model.metaType;
    this.isRequired = model.isRequired;
    this.filedDate = model.filedDate;
    this.interestPenaltyAmount = model.interestPenaltyAmount;
    this.year = model.year;

    this.documents = Document.mapModelsOrNull(model.documents);
    this.estateTask = EstateTask.mapModelOrNull(model.estateTask);
    this.meta = EstateTaxFormMeta.factory(model);
  }

  getModelName(): ModelType {
    return ModelType.ESTATE_TAX_FORM;
  }

  get hasDocuments(): boolean {
    return !!this.documents?.length;
  }

  get canEsign(): boolean {
    return DocumentSubType.canEsignTaxFormType(this.type);
  }

  get validDocument(): Document | undefined {
    return this.documents?.find((document: Document) => !document.isRejected);
  }

  get hasValidDocument(): boolean {
    return !!this.validDocument;
  }

  get canDeleteADocument(): boolean {
    return !!this.documents?.some((document: Document) => !document.hasEsignEnvelope);
  }

  get isReassessing(): boolean {
    return this.estateTask?.status === EstateTaskTaxFormStatus.REASSESSING.key;
  }

  get isAccepted(): boolean {
    return this.estateTask?.status === EstateTaskTaxFormStatus.ACCEPTED.key;
  }

  get isSubmittedToCRA(): boolean {
    return this.estateTask?.status === EstateTaskTaxFormStatus.SUBMITTED_TO_CRA.key;
  }

  get isWaitingForSignature(): boolean {
    return this.estateTask?.status === EstateTaskTaxFormStatus.WAITING_FOR_SIGNATURE.key;
  }

  get isNotApplicable(): boolean {
    return this.estateTask?.status === EstateTaskTaxFormStatus.NOT_APPLICABLE.key;
  }

  get canUploadNewDocument(): boolean {
    return this.isReassessing && this.hasValidDocument && !this.canDeleteADocument;
  }

  get isSignedByExecutor(): boolean {
    const executorEsignEnvelopeEstateAccount: EsignEnvelopeEstateAccount | undefined =
      this.validDocument?.esignEnvelope?.executorEsignEnvelopeEstateAccounts[0];
    return !!executorEsignEnvelopeEstateAccount?.isCompleted;
  }

  get isWaitingForExecutorSignature(): boolean {
    return this.isWaitingForSignature && !this.isSignedByExecutor;
  }

  get canChangeStatus(): boolean {
    return !this.isWaitingForExecutorSignature;
  }

  get isTenFortyOne(): boolean {
    return this.type === EstateTaxFormType.TEN_FORTY_ONE.key;
  }

  get isTenFortyOneAdjustment(): boolean {
    return this.type === EstateTaxFormType.TEN_FORTY_ONE_ADJUSTMENT.key;
  }

  get totalTaxAmount(): number {
    switch (this.metaType) {
      case EstateTaxFormMetaType.ESTATE_TAX_FORM_CANADA_META:
        return (this.meta as EstateTaxFormCanadaMeta).craAmount || 0;
      case EstateTaxFormMetaType.ESTATE_TAX_FORM_US_META:
        return (this.meta as EstateTaxFormUsMeta).irsAmount || 0;
      default:
        return 0;
    }
  }
}
